// Shared date-bucketing helpers — used by both `time-bucket` (verb)
// and `date-bucket` / `to-date` (scalar). SSOT for the period enum,
// the dayjs `startOf` unit mapping, and the dayjs UTC +
// quarterOfYear plugin installation. Importing this module installs
// the plugins as a side-effect; subsequent `dayjs.utc(...)` calls and
// `'quarter'` units work as expected.

import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear.js';
import utc from 'dayjs/plugin/utc.js';

dayjs.extend(utc);
dayjs.extend(quarterOfYear);

/** Period domain for `time-bucket` and `date-bucket`. */
export const Periods = ['minute', 'hour', 'day', 'week', 'month', 'quarter', 'year'] as const;
export type Period = (typeof Periods)[number];

/**
 * Period → dayjs `startOf` unit lookup. Same enum values, exposed
 * as a typed `Record` so callers can index without an `as` cast.
 */
export const PERIOD_TO_DAYJS: Record<Period, dayjs.OpUnitType | dayjs.QUnitType> = {
  minute: 'minute',
  hour: 'hour',
  day: 'day',
  week: 'week',
  month: 'month',
  quarter: 'quarter',
  year: 'year',
};

/**
 * Parse an `unknown` value to a `Date` in UTC mode, or `null` if
 * the value is null / invalid. ISO-8601 strings, epoch-ms numbers,
 * and `Date` values are accepted. SSOT for the "any input → Date"
 * coercion used by `to-date` and `bucketTimestamp`.
 */
export const toDateValue = (v: unknown): Date | null => {
  if (v === null || v === undefined) {
    return null;
  }
  if (v instanceof Date) {
    return v;
  }
  const d = dayjs.utc(v as never);
  return d.isValid() ? d.toDate() : null;
};
/**
 * Date-aware numeric coercion for the chain comparison ops
 * (`<`, `<=`, `>`, `>=`). `Number`-first, so numbers, numeric
 * strings, `Date` (→ epoch-ms), booleans and `null` keep their
 * exact prior semantics; only when `Number(v)` would be `NaN` do
 * we rescue ISO-8601 / offset date strings via `toDateValue` so a
 * `timestamp` column compares correctly against a string-literal
 * date bound. Genuinely non-numeric, non-date input still yields
 * `NaN` (comparison ⇒ false), matching the previous behaviour.
 * SSOT for "compare a value as a number, tolerating dates".
 */
export const toComparableNumber = (v: unknown): number => {
  const n = Number(v);
  if (!Number.isNaN(n)) {
    return n;
  }
  const d = toDateValue(v);
  return d ? d.getTime() : Number.NaN;
};
// Matches an ISO-8601-ish leading `YYYY-MM-DD` so we only treat
// genuinely date-shaped strings as dates — a bare numeric string
// like "100" is NOT date-like, preserving strict equality semantics.
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}/;

const isDateLike = (v: unknown): boolean => v instanceof Date || (typeof v === 'string' && ISO_DATE_RE.test(v));

/**
 * Asymmetric date-aware equality for `=` / `!=` / `in`. Strict
 * `===` first (so `(= 1 "1")`, `(= isActive "true")` stay `false`
 * per the documented strict-equality invariant — number/string/
 * boolean semantics are untouched). Only when at least one side is
 * genuinely date-shaped (a `Date`, or an ISO-`YYYY-MM-DD…` string)
 * do we bridge the gap by comparing parsed instants — so
 * `(= day "2025-10-01")` against a `timestamp` column (a `Date`
 * cell) does the obvious right thing instead of silently never
 * matching. SSOT for "are these two values the same instant".
 */
export const dateAwareEquals = (a: unknown, b: unknown): boolean => {
  if (a === b) {
    return true;
  }
  if (!isDateLike(a) && !isDateLike(b)) {
    return false;
  }
  const da = toDateValue(a);
  const db = toDateValue(b);
  return da !== null && db !== null && da.getTime() === db.getTime();
};

/**
 * Bucket a value to the start of `period` in UTC mode. Returns
 * `null` for invalid input. SSOT for the bucketing kernel shared
 * by `time-bucket` and `date-bucket`.
 */
export const bucketTimestamp = (v: unknown, period: Period): Date | null => {
  const d = toDateValue(v);
  if (d === null) {
    return null;
  }
  return dayjs.utc(d).startOf(PERIOD_TO_DAYJS[period]).toDate();
};

/**
 * Format a value (ISO string / epoch-ms / `Date`) as a string in UTC
 * mode using a dayjs pattern (default `YYYY-MM-DD`). Returns `null`
 * for null / invalid input. SSOT for the Date → display-string
 * direction — the inverse of `toDateValue`. UTC (not the display
 * timezone) so engine output is deterministic and environment-
 * independent, consistent with `toDateValue` / `bucketTimestamp`.
 */
export const formatDateValue = (v: unknown, pattern = 'YYYY-MM-DD'): string | null => {
  const d = toDateValue(v);
  return d === null ? null : dayjs.utc(d).format(pattern);
};

/**
 * Enumerate every period-start key in the half-open window
 * `[start, end)` at `period` granularity, UTC, ascending. SSOT for
 * the deterministic `:window` grid `time-bucket`'s per-cell form
 * emits: every lifted TS-ref cell — across every axis — produces a
 * row for every key here (gap buckets null-filled), so the lifted
 * columns share a byte-identical bucket set. That is the
 * positional-zip precondition multi-column `unnest` relies on
 * (PHASE_4_PREP.md D11.1/D11.2/D11.3). `start`/`end` are coerced via
 * `toDateValue`; an invalid bound or `end <= start` yields `[]`.
 */
export const periodGrid = (start: unknown, end: unknown, period: Period): readonly Date[] => {
  const s = toDateValue(start);
  const e = toDateValue(end);
  if (s === null || e === null) {
    return [];
  }
  const unit = PERIOD_TO_DAYJS[period];
  const stop = dayjs.utc(e);
  const out: Date[] = [];
  let cursor = dayjs.utc(s).startOf(unit);
  while (cursor.isBefore(stop)) {
    out.push(cursor.toDate());
    cursor = cursor.add(1, unit as dayjs.ManipulateType);
  }
  return out;
};
